
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { ROUTER } from '@/router/constants';
import { db, functions } from '@/firebase';
import {
	Company,
	DbTimestamp,
	formatDomainClaimDnsTxtRecord,
	isValidDomainForClaim,
	normalizeDomainForClaim,
	normalizeSsoExceptionEntry,
	SSO_EXCEPTION_WILDCARD_PREFIX,
	RUNDIFFUSION_SITE_VERIFICATION_TXT_KEY,
} from '@run-diffusion/shared';
import { EMAIL_REGEX, SNACKBAR_STATUS } from '@/constants/constants';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import TokenRevealDialog from '@/views/Team/components/TokenRevealDialog.vue';

export default Vue.extend({
	name: 'EditCompanySsoConfigurations',
	props: {
		enforceAuthProvider: { type: Boolean, default: false },
		/** SSO Exceptions (break-glass): normalized entry -> true. Owned and persisted by the parent. */
		emailExceptions: { type: Object, default: () => ({}) },
		saving: { type: Boolean, default: false },
	},
	data () {
		return {
			ROUTER,
			/** Open on arrival, collapsible by choice: once SSO is configured this is reference material. */
			ssoSectionOpen: true,
			loadingDomainClaimToken: false,
			loadingVerifyByDomain: {} as Record<string, boolean>,
			loadingRollDomainTokenByDomain: {} as Record<string, boolean>,
			loadingDeleteDomainClaimByDomain: {} as Record<string, boolean>,
			addingDomainClaim: false,
			newDomainClaimInput: '',
			addingEmailException: false,
			newEmailExceptionInput: '',
			domainClaimTokenModalOpen: false,
			domainClaimTokenValue: null as string | null,
			tokenRevealTitle: 'Copy your token',
			tokenRevealDescription: 'Copy this token now. You will not be able to see it again.',
			domainClaimHostForInstructions: '',
			/** Live `sso.domainClaims` from Firestore; Vuex `currCompany` can lag behind callable writes. */
			liveDomainClaims: null as Record<string, { last4?: string; isVerified?: boolean; verifiedAt?: DbTimestamp | null }> | null,
			liveDomainClaimsCompanyId: null as string | null,
			domainClaimsSnapshotUnsubscribe: null as null | (() => void),
		};
	},
	computed: {
		...mapState([
			'team',
			'user',
		]),
		...mapGetters([
			'companyClubOfferBenefits',
		]),
		currCompany (): Company {
			return this.team && typeof this.team.company !== 'string' ? this.team.company : null;
		},
		exampleEmailDomain (): string {
			return this.user?.email?.split('@')?.[1] || 'example.com';
		},
		ssoSignOnLink (): string {
			const identifier: string = this._get(this.currCompany, 'sso.identifier');
			if (!identifier) return '';
			return `${window.location.origin}/?autoLoginSsoProvider=${identifier}`;
		},
		rundiffusionSiteVerificationTxtKey (): string {
			return RUNDIFFUSION_SITE_VERIFICATION_TXT_KEY;
		},
		domainClaimItems (): Array<{ domain: string; last4: string; isVerified: boolean; verifiedAt: DbTimestamp | null }> {
			const companyId = this._get(this.currCompany, 'id') as string | undefined;
			const fromLive =
				companyId
				&& this.liveDomainClaimsCompanyId === companyId
				&& this.liveDomainClaims != null;
			const claims = fromLive
				? this.liveDomainClaims
				: (this._get(this.currCompany, 'sso.domainClaims') || {});
			return Object.keys(claims)
				.map((domain) => {
					const claim = claims[domain] || {};
					return {
						domain,
						last4: claim.last4 || '----',
						isVerified: !!claim.isVerified,
						verifiedAt: claim.verifiedAt || null,
					};
				})
				.sort((a, b) => a.domain.localeCompare(b.domain));
		},
		emailExceptionItems (): string[] {
			const m: Record<string, boolean> = (this.emailExceptions || {}) as Record<string, boolean>;
			return Object.keys(m)
				.filter((k) => m[k] === true)
				.sort((a: string, b: string) => a.localeCompare(b));
		},
	},
	watch: {
		currCompany: {
			immediate: true,
			handler (newVal: Company, oldVal: Company) {
				const newCompanyId: string = newVal?.id;
				const oldCompanyId: string = oldVal?.id;
				if (newCompanyId && oldCompanyId !== newCompanyId) {
					this.bindDomainClaimsSnapshot(newVal?.id);
				}
			},
		},
	},
	beforeDestroy () {
		this.unbindDomainClaimsSnapshot();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		unbindDomainClaimsSnapshot () {
			if (this.domainClaimsSnapshotUnsubscribe) {
				this.domainClaimsSnapshotUnsubscribe();
				this.domainClaimsSnapshotUnsubscribe = null;
			}
			this.liveDomainClaims = null;
			this.liveDomainClaimsCompanyId = null;
		},
		bindDomainClaimsSnapshot (companyId: string | undefined) {
			this.unbindDomainClaimsSnapshot();
			if (!companyId) return;
			this.liveDomainClaimsCompanyId = companyId;
			const ref = db.collection('companies').doc(companyId);
			this.domainClaimsSnapshotUnsubscribe = ref.onSnapshot(
				(snap) => {
					if (this.liveDomainClaimsCompanyId !== companyId) return;
					if (!snap.exists) {
						this.liveDomainClaims = {};
						return;
					}
					const raw = (snap.data()?.sso?.domainClaims || {}) as Record<string, { last4?: string; isVerified?: boolean; verifiedAt?: DbTimestamp | null }>;
					const claims: Record<string, { last4?: string; isVerified?: boolean; verifiedAt?: DbTimestamp | null }> = {};
					Object.keys(raw).forEach((d) => {
						claims[d] = { ...raw[d] };
					});
					this.liveDomainClaims = claims;
				},
				(err) => {
					console.error('[EditCompanySsoConfigurations] domainClaims snapshot', err);
				},
			);
		},
		openSsoConfigurationLink () {
			window.open('https://www.rundiffusion.com/how-to-configure-a-new-sso-configuration-with-microsoft-entra-id-azure-ad', '_blank');
		},
		// The parent owns the write, because it also owns the `saving` flag this component renders.
		onEnforceAuthProviderChange (value: boolean) {
			this.$emit('force-auth-provider-change', value);
		},
		onCancelCreateDomainClaim () {
			this.addingDomainClaim = false;
			this.newDomainClaimInput = '';
		},
		onAddEmailException () {
			const entry: string = normalizeSsoExceptionEntry(this.newEmailExceptionInput);
			const isWildcard: boolean = entry.indexOf(SSO_EXCEPTION_WILDCARD_PREFIX) === 0;
			const isValid: boolean = isWildcard
				? isValidDomainForClaim(entry.slice(SSO_EXCEPTION_WILDCARD_PREFIX.length))
				: EMAIL_REGEX.test(entry);
			if (!isValid) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Enter a valid email address (example@${this.exampleEmailDomain}) or a domain wildcard (*@${this.exampleEmailDomain})`,
					show: true,
				});
				return;
			}
			this.$emit('add-email-exception', entry);
			this.addingEmailException = false;
			this.newEmailExceptionInput = '';
		},
		onCancelAddEmailException () {
			this.addingEmailException = false;
			this.newEmailExceptionInput = '';
		},
		onConfirmRemoveEmailException (entry: string, closeDialog?: () => void) {
			this.$emit('remove-email-exception', entry);
			if (closeDialog) closeDialog();
		},
		presentDomainClaimTokenModal (domain: string, secretToken: string) {
			this.domainClaimHostForInstructions = domain;
			this.tokenRevealTitle = `DNS TXT for ${domain}`;
			this.tokenRevealDescription = 'Copy this verification value now. You will not be able to see it again.';
			this.domainClaimTokenValue = formatDomainClaimDnsTxtRecord(secretToken);
			this.domainClaimTokenModalOpen = true;
		},
		async onCreateDomainClaim () {
			const companyId: string = this._get(this.currCompany, 'id');
			const domain: string = normalizeDomainForClaim(this.newDomainClaimInput);
			if (!companyId || !isValidDomainForClaim(domain)) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please enter a valid domain (for example: example.com)',
					show: true,
				});
				return;
			}

			this.loadingDomainClaimToken = true;
			try {
				const fn = functions.httpsCallable('createOrRollAccessToken');
				const result = await fn({ companyId, useCase: 'domainClaim', domain });
				const data = result?.data as { success?: boolean; token?: string; message?: string };
				if (data?.success && data?.token) {
					this.presentDomainClaimTokenModal(domain, data.token);
					this.onCancelCreateDomainClaim();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Failed to create domain claim token',
						show: true,
					});
				}
			} catch (e: any) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e?.message || 'Failed to create domain claim token',
					show: true,
				});
			} finally {
				this.loadingDomainClaimToken = false;
			}
		},
		async onConfirmRollDomainToken (domain: string, closeDialog?: () => void) {
			const companyId = this._get(this.currCompany, 'id');
			if (!companyId) return;
			this.$set(this.loadingRollDomainTokenByDomain, domain, true);
			try {
				const fn = functions.httpsCallable('createOrRollAccessToken');
				const result = await fn({ companyId, useCase: 'domainClaim', domain });
				const data = result?.data as { success?: boolean; token?: string; message?: string };
				if (data?.success && data?.token) {
					this.presentDomainClaimTokenModal(domain, data.token);
					if (closeDialog) closeDialog();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Failed to roll domain claim token',
						show: true,
					});
				}
			} catch (e: any) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e?.message || 'Failed to roll domain claim token',
					show: true,
				});
			} finally {
				this.$set(this.loadingRollDomainTokenByDomain, domain, false);
			}
		},
		async onVerifyDomainClaim (domain: string) {
			const companyId = this._get(this.currCompany, 'id');
			if (!companyId) return;
			this.$set(this.loadingVerifyByDomain, domain, true);
			try {
				const fn = functions.httpsCallable('verifyDomainClaim');
				const result = await fn({ companyId, domain });
				const data = result?.data as { success?: boolean; message?: string };
				if (data?.success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `${domain} has been verified.`,
						show: true,
					});
					return;
				}
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: data?.message || 'Domain verification failed',
					show: true,
				});
			} catch (e: any) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e?.message || 'Domain verification failed',
					show: true,
				});
			} finally {
				this.$set(this.loadingVerifyByDomain, domain, false);
			}
		},
		async onConfirmDeleteDomainClaim (domain: string, closeDialog?: () => void) {
			const companyId = this._get(this.currCompany, 'id');
			if (!companyId) return;
			this.$set(this.loadingDeleteDomainClaimByDomain, domain, true);
			try {
				const fn = functions.httpsCallable('deleteDomainClaim');
				const result = await fn({ companyId, domain });
				const data = result?.data as { success?: boolean; message?: string };
				if (data?.success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Removed domain claim for ${domain}.`,
						show: true,
					});
					if (closeDialog) closeDialog();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Failed to remove domain claim',
						show: true,
					});
				}
			} catch (e: any) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e?.message || 'Failed to remove domain claim',
					show: true,
				});
			} finally {
				this.$set(this.loadingDeleteDomainClaimByDomain, domain, false);
			}
		},
	},
	components: {
		ConfirmDialog,
		ClipboardCopy,
		BaseToggle,
		TokenRevealDialog,
	},
});
